import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import { API_BASE, getImageUrl } from '../../../utils/api';

interface PriceHistory {
    month: string;
    year: number;
    avgPrice: number;
}

interface LocalityStats {
    name: string;
    avgPricePerSqFt: number;
    oldAvgPricePerSqFt: number;
    growthPercentage: number;
    growthScore: number;
    slug: string;
    image?: string;
    images?: string[];
    priceHistory: PriceHistory[];
}

interface PriceTrendsSectionProps {
    project: any;
    locationStats: LocalityStats | null;
    similarLocalities: any[];
}

const PriceTrendsSection: React.FC<PriceTrendsSectionProps> = ({ project, locationStats: initialStats, similarLocalities: initialSimilar }) => {
    const [timeRange, setTimeRange] = useState('5Y');
    const [localStats, setLocalStats] = useState<LocalityStats | null>(initialStats);
    const [similarLocs, setSimilarLocs] = useState<any[]>(initialSimilar || []);
    const [loading, setLoading] = useState(false);

    // Helper to parse price string from project overview (e.g., "5,500" or "\u20B9 4500/sqft")
    const parseProjectPrice = (priceStr?: string) => {
        if (!priceStr) return 0;
        // Check if it's a range like "4,950 - 5,400"
        const parts = priceStr.split(/[–-]/); // Split by hyphen or en-dash
        if (parts.length > 1) {
            const val1 = parseInt(parts[0].replace(/[^0-9]/g, ''));
            const val2 = parseInt(parts[1].replace(/[^0-9]/g, ''));
            if (!isNaN(val1) && !isNaN(val2)) {
                return Math.round((val1 + val2) / 2); // Return average of range
            }
            return isNaN(val1) ? 0 : val1;
        }
        const parsed = parseInt(priceStr.replace(/[^0-9]/g, ''));
        return isNaN(parsed) ? 0 : parsed;
    };

    const projectPrice = parseProjectPrice(project.overview?.avgPricePerSqFt);

    // Fetch locality trends if missing or empty
    useEffect(() => {
        const fetchLocalityData = async () => {
            if (!project.location) return;
            
            // Only fetch if we don't have stats or if the stats we have are effectively empty
            if (!initialStats || (initialStats.priceHistory && initialStats.priceHistory.length === 0)) {
                setLoading(true);
                try {
                    // Try to fetch by slug (slugified location name)
                    const slug = project.location.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
                    const res = await fetch(`${API_BASE}/locality-trends/slug/${slug}`);
                    const data = await res.json();
                    
                    if (data) {
                        if (data.locality) setLocalStats(data.locality);
                        if (data.similar) setSimilarLocs(data.similar);
                    }
                } catch (err) {
                    console.error('Failed to fetch dynamic locality stats:', err);
                } finally {
                    setLoading(false);
                }
            } else {
                setLocalStats(initialStats);
                setSimilarLocs(initialSimilar || []);
            }
        };

        fetchLocalityData();
    }, [project.location, initialStats, initialSimilar]);

    // Format Y-axis labels like \u20B9 4K
    const formatYAxis = (value: number) => {
        if (value >= 1000) return `\u20B9 ${(value / 1000).toFixed(0)}K`;
        return `\u20B9 ${value}`;
    };

    // Adaptive Dummy data generation based on project's own price
    const getDummyHistory = (base: number) => {
        const years = parseInt(timeRange);
        const data = [];
        const currentYear = new Date().getFullYear();
        const basePrice = base || 4500;
        
        for (let i = years; i >= 0; i--) {
            // Create a slight upward trend for the dummy data
            const trendFactor = 1 - (i * 0.05); // e.g. 5 years ago was ~75% of current
            const randomVolatility = Math.floor(Math.random() * 200) - 100;
            
            data.push({
                year: currentYear - i,
                avgPrice: Math.round(basePrice * trendFactor) + randomVolatility,
                month: 'Jan'
            });
        }
        return data;
    };

    const getFilteredHistory = () => {
        if (!localStats || !localStats.priceHistory || localStats.priceHistory.length === 0) {
            return getDummyHistory(projectPrice || localStats?.avgPricePerSqFt || 4500);
        }
        
        const history = [...localStats.priceHistory];
        const years = parseInt(timeRange);
        const months = years * 12;
        
        // If history is too short, supplement with dummy or just show what we have
        const sliced = history.slice(0, months).reverse();
        if (sliced.length < 2) return getDummyHistory(projectPrice || localStats.avgPricePerSqFt);
        
        return sliced;
    };

    const filteredHistory = getFilteredHistory();
    const chartData = filteredHistory.map(h => ({
        name: h.year.toString(),
        price: h.avgPrice,
        displayDate: `${h.month} ${h.year}`
    }));

    const currentPriceDisplay = projectPrice || localStats?.avgPricePerSqFt || 4450;
    const growthDisplay = localStats?.growthPercentage || 8.4;

    return (
        <section id="price-trends" className="mb-4 scroll-mt-32">
            <div className="card border border-gray-200 shadow-sm rounded-2xl overflow-hidden bg-white">
                <div className="bg-white py-4 px-6 md:px-8 border-b border-gray-200">
                    <h4 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95] leading-tight uppercase tracking-wide">Price Trend in {localStats?.name || project.location}</h4>
                </div>

                <div className="p-6 md:p-8 pb-0">
                    {/* Premium Locality Hero Banner - Now at the top of content */}
                    {(localStats?.image || (localStats?.images && localStats.images.length > 0)) && (
                        <div className="mb-8">
                            <div className="relative group overflow-hidden rounded-2xl shadow-xl border border-slate-100">
                                {localStats.images && localStats.images.length > 0 ? (
                                    <div className="flex gap-4 overflow-x-auto no-scrollbar p-2">
                                        {/* Main Image first */}
                                        {localStats.image && (
                                            <div className="min-w-[320px] md:min-w-[550px] h-[200px] md:h-[300px] rounded-xl overflow-hidden relative shadow-lg group-hover:shadow-2xl transition-all duration-700">
                                                <img 
                                                    src={getImageUrl(localStats.image)} 
                                                    alt={localStats.name} 
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" 
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                                            </div>
                                        )}
                                        {/* Gallery Images */}
                                        {localStats.images.map((img, idx) => (
                                            <div key={idx} className="min-w-[200px] md:min-w-[350px] h-[200px] md:h-[300px] rounded-xl overflow-hidden shadow-md">
                                                <img src={getImageUrl(img)} alt={`${localStats.name} view ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    /* Full Hero Banner Style */
                                    localStats.image && (
                                        <div className="relative h-[250px] md:h-[350px] group transition-all duration-700">
                                            <img 
                                                src={getImageUrl(localStats.image)} 
                                                alt={localStats.name} 
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2000ms]" 
                                            />
                                            {/* Premium Gradient Overlays */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    )}

                    {/* Top Stats Section */}
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                        <div>
                            <h3 className="text-black font-bold text-[13px] md:text-[14px] mb-2 leading-tight">
                                RATE ON MyPropertyGuru <br />
                                <span className="font-medium text-black text-[11px] md:text-[12px]">(Super Built up Area)</span>
                            </h3>
                            <div className="flex flex-wrap items-baseline gap-2">
                                <span className="text-3xl md:text-4xl font-bold text-black">
                                    {project.overview?.avgPricePerSqFt ? project.overview.avgPricePerSqFt : `₹ ${currentPriceDisplay.toLocaleString('en-IN')}`}
                                </span>
                                {!project.overview?.avgPricePerSqFt && (
                                    <span className="text-slate-500 font-bold text-base md:text-lg">/ sq.ft</span>
                                )}
                                {!localStats && !loading && (
                                    <span className="text-[12px] bg-slate-100 text-slate-600 px-3 py-1 rounded-full font-medium h-fit">Market Estimate</span>
                                )}
                            </div>
                        </div>

                        {/* Integrated Growth Badge */}
                        <div className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-2xl text-[13px] md:text-[14px] font-bold flex items-center gap-2 border border-emerald-100">
                            <i className="fa-solid fa-arrow-trend-up text-sm"></i>
                            <span>{growthDisplay}% GROWTH IN LAST 5 YEARS</span>
                        </div>
                    </div>

                    {/* Nearby Areas Carousel */}
                    {similarLocs.length > 0 && (
                        <div className="mb-10">
                            <div className="flex items-center justify-between mb-4">
                                <h5 className="text-[13px] font-bold text-black tracking-wide">Nearby Area Trends</h5>
                                <div className="flex gap-2">
                                    <button className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 transition-colors">
                                        <i className="fa-solid fa-chevron-left text-[10px]"></i>
                                    </button>
                                    <button className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
                                        <i className="fa-solid fa-chevron-right text-[10px]"></i>
                                    </button>
                                </div>
                            </div>
                            
                            <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
                                {similarLocs.map((loc, idx) => (
                                    <Link 
                                        key={idx} 
                                        to={`/location/${loc.slug || '#'}`}
                                        className="min-w-[180px] group cursor-pointer"
                                    >
                                        <div className="relative h-28 rounded-xl overflow-hidden mb-3 border border-slate-100 shadow-sm transition-transform group-hover:scale-[1.02] duration-300">
                                            {loc.image ? (
                                                <img src={getImageUrl(loc.image)} alt={loc.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                                                    <i className="fa-solid fa-building text-slate-300 text-2xl"></i>
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                            <div className="absolute bottom-2 left-3 right-3">
                                                <p className="text-white text-[13px] font-bold truncate mb-0.5">{loc.name}</p>
                                                <p className="text-emerald-400 text-[11px] font-medium flex items-center gap-1">
                                                    <i className="fa-solid fa-arrow-trend-up"></i>
                                                    {loc.growthPercentage || 0}% Growth
                                                </p>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Chart Container */}
                    <div className={`bg-slate-50/50 -mx-6 md:-mx-8 py-4 md:py-6 border-y border-slate-100 relative transition-opacity duration-500 ${loading ? 'opacity-50' : 'opacity-100'}`}>
                        {/* Chart Header */}
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8 px-4 md:px-6">
                            <div>
                                <h4 className="text-black font-bold text-[14px] md:text-[15px]">AVG. PROPERTY RATE</h4>
                                {localStats?.name && (
                                    <p className="text-[13px] md:text-[14px] font-medium text-black mt-1">{localStats.name} Price Analysis</p>
                                )}
                            </div>
                            
                            <div className="relative group">
                                <select 
                                    value={timeRange}
                                    onChange={(e) => setTimeRange(e.target.value)}
                                    className="appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2 pr-10 text-[13px] font-bold text-black cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-500/20 shadow-sm"
                                >
                                    <option value="1Y">Last 1 Year</option>
                                    <option value="3Y">Last 3 Years</option>
                                    <option value="5Y">Last 5 Years</option>
                                    <option value="10Y">Last 10 Years</option>
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#1e293b]">
                                    <i className="fa-solid fa-chevron-down text-[10px]"></i>
                                </div>
                            </div>
                        </div>

                        {/* Main Price Trend Chart */}
                        <div className="h-[180px] md:h-[240px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#4c1d95" stopOpacity={0.2}/>
                                            <stop offset="95%" stopColor="#4c1d95" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="name" 
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                        tickMargin={15}
                                    />
                                    <YAxis 
                                        tickFormatter={formatYAxis}
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                        domain={['auto', 'auto']}
                                        tickCount={5}
                                    />
                                    <Tooltip 
                                        cursor={{ stroke: '#4c1d95', strokeWidth: 1, strokeDasharray: '5 5' }}
                                        contentStyle={{ 
                                            borderRadius: '16px', 
                                            border: 'none', 
                                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                                            padding: '12px'
                                        }}
                                        itemStyle={{ color: '#05144b', fontWeight: 900, fontSize: '14px' }}
                                        labelStyle={{ color: '#64748b', fontSize: '10px', marginBottom: '6px', textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.1em' }}
                                        formatter={(value: any) => [`₹ ${value.toLocaleString('en-IN')}`, 'Rate']}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="price" 
                                        stroke="#05144b" 
                                        strokeWidth={3}
                                        fillOpacity={1} 
                                        fill="url(#colorPrice)"
                                        animationDuration={1500}
                                        activeDot={{ r: 6, stroke: '#fff', strokeWidth: 3, fill: '#4c1d95' }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                        {loading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm rounded-2xl z-10">
                                <div className="w-10 h-10 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

        </section>
    );
};

export default PriceTrendsSection;
